import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * @returns {HTMLElement | null}
   */
  get headerTitle() {
    return /** @type {HTMLElement | null} */ (this.querySelector('#MenuDrawerTitle'));
  }

  /**
   * @returns {HTMLElement | null}
   */
  get backButton() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.menu-drawer__back-header'));
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  updateHeader(details) {
    if (!this.headerTitle || !this.backButton) return;

    // Root menu
    if (details === this.refs.details) {
      this.headerTitle.textContent = this.headerTitle.dataset.defaultTitle ?? null;
      this.backButton.hidden = true;
      return;
    }

    // Current submenu title
    const summary = details.querySelector(':scope > summary');
    const title = summary
      ?.querySelector('.menu-drawer__menu-item-text')
      ?.textContent
      ?.trim();

    if (title) {
      this.headerTitle.textContent = title;
    }

    this.backButton.hidden = false;
  }


  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.backButton?.addEventListener('click', this.#onBackButtonClick);
    this.#setupAnimatedElementListeners();
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.backButton?.removeEventListener('click', this.#onBackButtonClick);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * Always show the drawer closed when the page is restored from bfcache
   * @param {PageTransitionEvent} event
   */
  #onPageShow = (event) => {
    if (!event.persisted) return;

    this.querySelectorAll('details[open], details.menu-open').forEach(reset);
    reset(this.refs.details);
    this.updateHeader(this.refs.details);
    removeTrapFocus();
  };

  /**
   * Close the deepest open submenu when the back button is clicked
   */
  #onBackButtonClick = () => {
    const openMenus = this.querySelectorAll('details.menu-open');
    const current = openMenus[openMenus.length - 1];

    if (current instanceof HTMLDetailsElement) {
      this.#close(current);
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.updateHeader(details);

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      details.querySelectorAll('accordion-custom > details[open]').forEach(reset);

      if (details === this.refs.details) {
        this.updateHeader(this.refs.details);

        removeTrapFocus();

        const openDetails = this.querySelectorAll(
          'details[open]:not(accordion-custom > details)'
        );

        openDetails.forEach(reset);

      } else {
        const parentDetails = details.parentElement?.closest('details');

        if (parentDetails) {
          this.updateHeader(parentDetails);
        } else {
          this.updateHeader(this.refs.details);
        }

        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {Element} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
